/**
 * ParentProgressPage.js — Teen Progress: usage frequency (how often they
 * use HiBFF), streaks, badges, NFT pivot meter, challenge timeline, and the
 * stealth "encouraging nudge" (worded from Maya — never mentions the parent).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  TrendingUp, Sparkles, Loader2, CalendarDays, Flame, Clock, Award,
  Gem, Send, CheckCircle,
} from 'lucide-react';
import { ParentShell } from '../components/parent/ParentShell';
import { apiClient } from '../api/client';

const NUDGE_OPTIONS = [
  { type: 'continue', label: 'Keep practising' },
  { type: 'maya_help', label: "Maya's here to help" },
  { type: 'proud', label: 'Quiet encouragement' },
];

const lastSeenLabel = (iso) => {
  if (!iso) return 'Not yet';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

const NudgeBox = ({ teen, onSent }) => {
  const [type, setType] = useState('continue');
  const [state, setState] = useState('idle');
  const send = async () => {
    setState('sending');
    try {
      const r = await apiClient.request('/api/parent/nudge-teen', {
        method: 'POST', body: JSON.stringify({ teen_id: teen.teen_id, nudge_type: type }),
      });
      setState('sent');
      onSent(r.channel);
    } catch (e) {
      alert(e?.message || 'Could not send nudge.');
      setState('idle');
    }
  };
  if (!teen.can_nudge || state === 'sent') {
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-700 font-semibold" data-testid={`nudge-done-${teen.teen_id}`}>
        <CheckCircle size={16} /> {state === 'sent' ? 'Nudge sent — worded from Maya, never from you.' : 'Nudge sent today — next one unlocks tomorrow.'}
      </div>
    );
  }
  return (
    <div data-testid={`nudge-box-${teen.teen_id}`}>
      <p className="text-sm font-semibold text-gray-900 mb-1">Send an encouraging nudge</p>
      <p className="text-xs text-gray-500 mb-3">
        Delivered to {teen.teen_first_name}&apos;s phone{teen.has_phone ? '' : ' (in-app — no mobile saved)'} as a friendly note from Maya.
        <span className="font-semibold"> It never mentions you.</span> One per day.
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        {NUDGE_OPTIONS.map(o => (
          <button key={o.type} onClick={() => setType(o.type)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition ${type === o.type ? 'bg-violet-600 border-violet-600 text-white' : 'border-gray-200 text-gray-600 hover:border-violet-300'}`}
            data-testid={`nudge-type-${teen.teen_id}-${o.type}`}>
            {o.label}
          </button>
        ))}
      </div>
      <button onClick={send} disabled={state === 'sending'}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-bold disabled:opacity-50"
        data-testid={`nudge-send-${teen.teen_id}`}>
        {state === 'sending' ? <Loader2 size={16} className="animate-spin" /> : <Send size={14} />}
        Send stealth nudge
      </button>
    </div>
  );
};

const UsageStat = ({ icon: Icon, value, label, tone }) => (
  <div className={`rounded-2xl p-4 text-center ${tone}`}>
    <Icon size={18} className="mx-auto mb-1 opacity-70" />
    <p className="text-2xl font-black">{value}</p>
    <p className="text-[10px] uppercase tracking-widest font-bold opacity-70 mt-0.5">{label}</p>
  </div>
);

const ParentProgressPage = () => {
  const [insights, setInsights] = useState(null);
  const [dash, setDash] = useState(null);

  useEffect(() => {
    (async () => {
      const [i, d] = await Promise.all([
        apiClient.request('/api/parent/insights').catch(() => ({ teens: [] })),
        apiClient.request('/api/parent/custom-dashboard').catch(() => ({ teens: [] })),
      ]);
      setInsights(i?.teens || []);
      setDash(d);
    })();
  }, []);

  const allChallenges = useMemo(() => {
    const teens = dash?.teens || [];
    return teens.flatMap(t => (t.completed_challenges || []).map(c => ({ ...c, teen: t.teen_first_name })));
  }, [dash]);

  return (
    <ParentShell activeId="progress" title="Teen Progress">
      <div className="px-6 lg:px-10 py-8 lg:py-10 space-y-8 max-w-5xl">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white shadow-lg">
            <TrendingUp size={22} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Teen Progress</h2>
            <p className="text-sm text-gray-500">How often they practise, what they&apos;ve earned, and how close the NFT pivot is.</p>
          </div>
        </div>

        {insights === null ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-emerald-500" size={28} /></div>
        ) : insights.length === 0 ? (
          <p className="text-sm text-gray-400 py-8 text-center border-2 border-dashed border-gray-200 rounded-2xl">
            No linked teens yet — sign the Charter from your Dashboard to link your teen.
          </p>
        ) : insights.map(t => (
          <section key={t.teen_id} className="rounded-3xl bg-white border border-gray-100 p-7 shadow-sm space-y-6" data-testid={`progress-teen-${t.teen_id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h3 className="font-bold text-gray-900 text-xl">{t.teen_first_name}</h3>
              <span className="text-xs text-gray-500 inline-flex items-center gap-1.5">
                <Clock size={13} /> Last seen: <strong className="text-gray-800">{lastSeenLabel(t.usage.last_active)}</strong>
              </span>
            </div>

            {/* Usage frequency — how often the teen uses HiBFF */}
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 font-bold mb-2">How often {t.teen_first_name} uses HiBFF</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid={`usage-stats-${t.teen_id}`}>
                <UsageStat icon={CalendarDays} value={`${t.usage.days_active_7}/7`} label="Days this week" tone="bg-emerald-50 text-emerald-800" />
                <UsageStat icon={CalendarDays} value={`${t.usage.days_active_30}/30`} label="Days this month" tone="bg-teal-50 text-teal-800" />
                <UsageStat icon={Sparkles} value={t.usage.sessions_30} label="Sessions (30d)" tone="bg-sky-50 text-sky-800" />
                <UsageStat icon={Flame} value={t.usage.current_streak} label="Current streak" tone="bg-orange-50 text-orange-800" />
              </div>
            </div>

            {/* Earned */}
            <div className="grid grid-cols-3 gap-3">
              <UsageStat icon={Award} value={t.badges_earned} label="Badges earned" tone="bg-indigo-50 text-indigo-800" />
              <UsageStat icon={TrendingUp} value={t.challenges_completed} label="Challenges" tone="bg-violet-50 text-violet-800" />
              <UsageStat icon={Gem} value={t.vault_items} label="Vault items" tone="bg-fuchsia-50 text-fuchsia-800" />
            </div>

            {/* NFT pivot meter */}
            <div data-testid={`pivot-meter-${t.teen_id}`}>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 font-bold">Pivot point: virtual badges → real NFTs</p>
                <span className="text-xs font-bold text-gray-700">{t.pivot.reached ? 'Reached! 🎉' : `${t.pivot.pct}%`}</span>
              </div>
              <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-700 ${t.pivot.reached ? 'bg-gradient-to-r from-fuchsia-500 to-violet-500' : 'bg-gradient-to-r from-emerald-400 to-teal-500'}`}
                  style={{ width: `${t.pivot.pct}%` }} />
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                {t.pivot.reached
                  ? 'They crossed the pivot — their collectibles are now real NFTs in the making. You get a text at the first mint.'
                  : `${t.pivot.badges} of ${t.pivot.target} badges toward the pivot. You'll get a text the moment they cross over.`}
              </p>
            </div>

            {/* Stealth nudge */}
            <div className="rounded-2xl bg-violet-50/60 border border-violet-100 p-5">
              <NudgeBox teen={t} onSent={() => {}} />
            </div>
          </section>
        ))}

        {/* Challenge timeline */}
        {insights !== null && insights.length > 0 && (
          <section className="rounded-3xl bg-white border border-gray-100 p-7 shadow-sm">
            <h3 className="font-bold text-gray-900 text-lg mb-1">Completed Challenges Timeline</h3>
            <p className="text-sm text-gray-500 mb-5">Every real-world challenge beaten, most recent first.</p>
            {allChallenges.length === 0 ? (
              <p className="text-gray-400 text-sm py-8 text-center">No challenges completed yet. Their first run will land here.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {allChallenges.map((c, i) => (
                  <li key={i} className="py-4 flex items-center justify-between gap-4" data-testid={`progress-row-${i}`}>
                    <div className="flex items-center gap-4 min-w-0">
                      <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 font-mono font-bold flex items-center justify-center">
                        {String(c.sequence ?? '·').padStart(2, '0')}
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{c.title}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1.5"><Sparkles size={11} /> {c.teen}</p>
                      </div>
                    </div>
                    <span className="flex-shrink-0 text-sm font-semibold text-gray-600 tracking-tight">{c.duration_label}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </ParentShell>
  );
};

export default ParentProgressPage;
